import React, { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { SectionTitle } from "@/components/dashboard/widgets";
import { MessageSquare, Mail, Loader2, Send, Sparkles, ShieldCheck, AlertCircle } from "lucide-react";
import ReplyTriageProfile from "@/components/dashboard/ReplyTriageProfile";

/**
 * Auto Follow-Up settings + activity (iter77).
 *
 * Buyer page. One toggle to enable. Editable SMS / email templates with merge
 * tags. Daily + monthly spend caps. Test send to the buyer's own
 * phone/email on file. Recent activity log with status + cost.
 *
 * Brand: premium navy + slate. Sharp edges, no traffic lights. £0.05 per lead
 * bundles both SMS + email — communicated cleanly in the price chip.
 */
const POUND = (pence) => `£${(pence / 100).toFixed(2)}`;
const MERGE_TAGS = [
  { tag: "{first_name}",   label: "Lead's first name" },
  { tag: "{buyer_company}",label: "Your company name" },
  { tag: "{advisor_name}", label: "Advisor name (set below)" },
  { tag: "{sector}",       label: "Lead sector" },
];

export default function AutoFollowUp() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/client/followup");
      setData(r.data);
      setForm({
        enabled:           !!r.data.settings.enabled,
        sms_template:      r.data.settings.sms_template || "",
        email_subject:     r.data.settings.email_subject || "",
        email_body:        r.data.settings.email_body || "",
        advisor_name:      r.data.settings.advisor_name || "Advisor",
        daily_cap_pence:   r.data.settings.daily_cap_pence ?? 5000,
        monthly_cap_pence: r.data.settings.monthly_cap_pence ?? 100000,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load settings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form) return;
    setSaving(true);
    try {
      const r = await api.put("/client/followup", form);
      setData((d) => ({ ...d, settings: r.data.settings }));
      toast.success("Settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const r = await api.post("/client/followup/test");
      const sms = r.data?.sms || "";
      const email = r.data?.email || "";
      const msg = `SMS: ${sms} · Email: ${email}`;
      if ((sms === "sent" || sms.startsWith("no_")) && (email === "sent" || email.startsWith("no_"))) {
        toast.success(msg);
      } else {
        toast.warning(msg);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test send failed");
    } finally {
      setTesting(false);
    }
  };

  if (loading || !form || !data) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  const dailyPct = Math.min(100, ((data.today_spent_pence || 0) / Math.max(1, form.daily_cap_pence)) * 100);
  const monthPct = Math.min(100, ((data.month_spent_pence || 0) / Math.max(1, form.monthly_cap_pence)) * 100);

  return (
    <div className="space-y-6">
      <SectionTitle
        eyebrow="Lead conversion · addon"
        title="Auto Follow-Up"
        subtitle="Send every accepted lead an instant SMS + email from your team — within 60 seconds. Pickup rates jump, returns drop."
        actions={
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="rounded-md text-[11.5px] border-slate-200 font-medium">
              £0.05 per accepted lead · both channels
            </Badge>
          </div>
        }
      />

      {/* Master toggle + spend caps */}
      <Card className="rounded-xl border-slate-200 shadow-soft" data-testid="followup-master-card">
        <CardContent className="p-6">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-3 min-w-0">
              <span className="inline-grid place-items-center size-10 rounded-md bg-[#0F172A] text-white shrink-0">
                <Sparkles className="size-4" />
              </span>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="text-[15px] font-semibold text-slate-900">Auto Follow-Up</h3>
                  {form.enabled ? (
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.12em] font-semibold px-1.5 py-0.5 rounded text-emerald-700 bg-emerald-500/10 border border-emerald-200">
                      <span className="size-1.5 rounded-full bg-emerald-500" /> Active
                    </span>
                  ) : (
                    <span className="text-[10px] uppercase tracking-[0.12em] font-semibold text-slate-500">Inactive</span>
                  )}
                </div>
                <p className="text-[12.5px] text-slate-500 mt-1 max-w-2xl leading-relaxed">
                  When ON, every newly accepted lead gets an instant SMS and email from your advisor. £0.05 is debited from your wallet per lead (bundled — both channels). Spend caps below keep you in control.
                </p>
              </div>
            </div>
            <Switch
              checked={form.enabled}
              onCheckedChange={(v) => setForm({ ...form, enabled: v })}
              data-testid="followup-toggle"
              className="mt-1"
            />
          </div>

          {/* Spend dashboard */}
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-3">
            <SpendCard
              label="Today"
              spent={data.today_spent_pence || 0}
              cap={form.daily_cap_pence}
              pct={dailyPct}
              onCapChange={(v) => setForm({ ...form, daily_cap_pence: v })}
              testid="followup-daily-cap"
            />
            <SpendCard
              label="This month"
              spent={data.month_spent_pence || 0}
              cap={form.monthly_cap_pence}
              pct={monthPct}
              onCapChange={(v) => setForm({ ...form, monthly_cap_pence: v })}
              testid="followup-monthly-cap"
            />
          </div>
        </CardContent>
      </Card>

      {/* Templates */}
      <Card className="rounded-xl border-slate-200 shadow-soft">
        <CardContent className="p-6 space-y-5">
          <div>
            <h3 className="text-[14.5px] font-semibold text-slate-900">Message templates</h3>
            <p className="text-[12px] text-slate-500 mt-1">
              Use merge tags so each lead gets a personal message. Tags resolve to the lead's data at send-time.
            </p>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {MERGE_TAGS.map((t) => (
                <span key={t.tag} className="inline-flex items-center gap-1 text-[11px] font-mono px-2 py-0.5 rounded bg-slate-100 text-slate-700 border border-slate-200" title={t.label}>
                  {t.tag}
                </span>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="advisor_name" className="text-[11.5px] uppercase tracking-[0.1em] font-semibold text-slate-600">Advisor name</Label>
            <Input
              id="advisor_name"
              value={form.advisor_name}
              onChange={(e) => setForm({ ...form, advisor_name: e.target.value })}
              className="rounded-md h-9 text-[13px] border-slate-200 focus-visible:ring-1 focus-visible:ring-[#0F172A] max-w-md"
              data-testid="followup-advisor-name"
              placeholder="e.g. Mark Connolly"
            />
            <p className="text-[11px] text-slate-500">Shown as the sender in templates — keep it personal.</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* SMS */}
            <div className="rounded-md border border-slate-200 p-4 space-y-3 bg-white">
              <div className="flex items-center gap-2 text-slate-900">
                <MessageSquare className="size-3.5" />
                <span className="text-[13px] font-semibold">SMS</span>
                <span className="text-[10.5px] text-slate-500">Max 160 chars per send</span>
              </div>
              <Textarea
                rows={4}
                value={form.sms_template}
                onChange={(e) => setForm({ ...form, sms_template: e.target.value })}
                className="font-mono text-[12.5px] border-slate-200 focus-visible:ring-1 focus-visible:ring-[#0F172A] resize-none"
                placeholder="Hi {first_name}, this is {advisor_name} at {buyer_company}…"
                data-testid="followup-sms-template"
              />
              <div className="text-[11px] text-slate-500 tabular-nums">{(form.sms_template || "").length} chars</div>
            </div>
            {/* Email */}
            <div className="rounded-md border border-slate-200 p-4 space-y-3 bg-white">
              <div className="flex items-center gap-2 text-slate-900">
                <Mail className="size-3.5" />
                <span className="text-[13px] font-semibold">Email</span>
                <span className="text-[10.5px] text-slate-500">Free with SMS — bundled</span>
              </div>
              <Input
                value={form.email_subject}
                onChange={(e) => setForm({ ...form, email_subject: e.target.value })}
                className="rounded-md h-9 text-[12.5px] border-slate-200 focus-visible:ring-1 focus-visible:ring-[#0F172A]"
                placeholder="Subject — {sector} enquiry from {buyer_company}"
                data-testid="followup-email-subject"
              />
              <Textarea
                rows={6}
                value={form.email_body}
                onChange={(e) => setForm({ ...form, email_body: e.target.value })}
                className="font-mono text-[12px] border-slate-200 focus-visible:ring-1 focus-visible:ring-[#0F172A] resize-none"
                data-testid="followup-email-body"
              />
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-end gap-2 pt-2 border-t border-slate-100">
            <Button
              variant="outline"
              onClick={sendTest}
              disabled={testing}
              className="rounded-md h-9 px-4 text-[12.5px]"
              data-testid="followup-test-send"
            >
              {testing ? <Loader2 className="size-3.5 animate-spin" /> : <Send className="size-3.5" />}
              Send me a test
            </Button>
            <Button
              onClick={save}
              disabled={saving}
              className="rounded-md bg-[#0F172A] hover:bg-[#1e293b] text-white h-9 px-5 text-[13px] font-medium"
              data-testid="followup-save"
            >
              {saving ? <Loader2 className="size-3.5 animate-spin" /> : "Save changes"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Compliance disclosure */}
      <Card className="rounded-xl border-slate-200 shadow-soft bg-slate-50/50">
        <CardContent className="p-5 flex items-start gap-3">
          <ShieldCheck className="size-4 text-slate-600 shrink-0 mt-0.5" />
          <div className="text-[12px] text-slate-600 leading-relaxed">
            Auto Follow-Up only fires for leads where the lead has actively opted in via a Connexis lander (the same consent basis under which you purchase the lead). Messages include a standard STOP opt-out line. Sender ID is your registered company name. SMS sends are logged and immutable for compliance audit. <span className="font-medium text-slate-800">By enabling this feature you confirm you will only contact leads in line with PECR/GDPR rules.</span>
          </div>
        </CardContent>
      </Card>

      {/* Activity */}
      <Card className="rounded-xl border-slate-200 shadow-soft">
        <CardContent className="p-6">
          <div className="flex items-baseline justify-between gap-2 flex-wrap">
            <div>
              <h3 className="text-[14.5px] font-semibold text-slate-900">Recent activity</h3>
              <p className="text-[12px] text-slate-500 mt-0.5">Last {data.recent?.length || 0} sends · this month: <span className="text-slate-800 font-semibold">{data.month_stats?.sent || 0} sent</span> · <span className="text-slate-700">{data.month_stats?.failed || 0} failed</span> · <span className="text-slate-500">{data.month_stats?.skipped || 0} skipped</span></p>
            </div>
            <Badge variant="outline" className="rounded-md text-[11px] border-slate-200">
              {POUND(data.month_spent_pence || 0)} spent this month
            </Badge>
          </div>

          {(data.recent || []).length === 0 ? (
            <div className="mt-5 rounded-md border border-dashed border-slate-200 py-10 text-center text-[12.5px] text-slate-500">
              No follow-ups sent yet. Toggle this addon on, then accept a lead to see activity stream here.
            </div>
          ) : (
            <div className="mt-5 overflow-hidden rounded-md border border-slate-200">
              <div className="overflow-x-auto">
                <table className="w-full text-[12.5px]">
                  <thead className="bg-slate-50/70 border-b border-slate-200">
                    <tr className="text-left text-[10.5px] uppercase tracking-[0.1em] text-slate-500 font-semibold">
                      <th className="py-2.5 px-3">When</th>
                      <th className="py-2.5 px-3">Channel</th>
                      <th className="py-2.5 px-3">Status</th>
                      <th className="py-2.5 px-3 text-right">Cost</th>
                      <th className="py-2.5 px-3">Note</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data.recent || []).map((s, i) => {
                      const dt = new Date(s.sent_at);
                      return (
                        <tr key={s.id} className={i % 2 === 0 ? "bg-white" : "bg-slate-50/40"} data-testid={`followup-row-${s.id}`}>
                          <td className="py-2 px-3 text-slate-700 tabular-nums">{dt.toLocaleDateString()} <span className="text-slate-400">{dt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span></td>
                          <td className="py-2 px-3 text-slate-700 capitalize">{s.channel === "sms" ? <MessageSquare className="size-3 inline -mt-0.5 mr-1" /> : <Mail className="size-3 inline -mt-0.5 mr-1" />}{s.channel}</td>
                          <td className="py-2 px-3">
                            {s.status === "sent" && <span className="inline-flex items-center gap-1 text-emerald-700 text-[11.5px] font-medium"><span className="size-1.5 rounded-full bg-emerald-500" /> Sent</span>}
                            {s.status === "failed" && <span className="inline-flex items-center gap-1 text-rose-600 text-[11.5px] font-medium"><AlertCircle className="size-3" /> Failed</span>}
                            {s.status === "skipped" && <span className="inline-flex items-center gap-1 text-slate-500 text-[11.5px] font-medium">Skipped</span>}
                          </td>
                          <td className="py-2 px-3 text-slate-900 font-semibold tabular-nums text-right">{s.cost_pence ? POUND(s.cost_pence) : "—"}</td>
                          <td className="py-2 px-3 text-slate-500 text-[11.5px] truncate max-w-xs" title={s.reason || s.body_preview || ""}>{s.reason || s.body_preview || ""}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* AI Reply Triage section — qualification profile + test */}
      <ReplyTriageProfile />
    </div>
  );
}

function SpendCard({ label, spent, cap, pct, onCapChange, testid }) {
  return (
    <div className="rounded-md border border-slate-200 p-4 bg-white">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[10.5px] uppercase tracking-[0.1em] font-semibold text-slate-500">{label} cap</span>
        <span className="text-[12px] text-slate-700 tabular-nums">{POUND(spent)} / <span className="text-slate-500">{POUND(cap)}</span></span>
      </div>
      <div className="mt-2 h-1.5 rounded-full bg-slate-100 overflow-hidden">
        <div className={`h-full rounded-full ${pct > 90 ? "bg-rose-500" : pct > 75 ? "bg-amber-500" : "bg-[#0F172A]"}`} style={{ width: `${pct}%` }} />
      </div>
      <div className="mt-3 flex items-center gap-2">
        <span className="text-[10.5px] text-slate-500">Cap (£):</span>
        <Input
          type="number"
          min={0}
          step={5}
          value={(cap / 100).toFixed(0)}
          onChange={(e) => onCapChange(Math.max(0, Math.round(parseFloat(e.target.value || "0") * 100)))}
          className="rounded-md h-7 text-[12px] w-24 border-slate-200 focus-visible:ring-1 focus-visible:ring-[#0F172A]"
          data-testid={testid}
        />
      </div>
    </div>
  );
}
